import React from 'react';

import { useManagementPackContext } from '../../ManagementPackContext';

import { Group, Toggle } from './OptionControls';

/**
 * The appendix parts, by letter. Letters are fixed whichever parts a pack
 * includes.
 */
const LETTERS = () => [
    { letter: 'A0', parts: [{ key: 'statuses', label: $t('A0 Statuses set manually') }] },
    { letter: 'A1', parts: [{ key: 'budget_lines', label: $t('A1 Budget by department and cost centre') }] },
    { letter: 'A2', parts: [{ key: 'budget_changes', label: $t('A2 All budget changes in the period') }] },
    { letter: 'A3', parts: [{ key: 'weighted_risks', label: $t('A3 Weighted risk and opportunity table') }] },
    {
        letter: 'A4',
        parts: [
            { key: 'risk_changes', label: $t('A4 Risks with changed assessment') },
            { key: 'risk_register', label: $t('A4 Full risk register') },
        ],
    },
    { letter: 'A5', parts: [{ key: 'procurement', label: $t('A5 Procurement register') }] },
    {
        letter: 'A6',
        parts: [
            { key: 'org_charts', label: $t('A6 Organisation charts') },
            { key: 'positions', label: $t('A6 Position list') },
        ],
    },
    { letter: 'A7', parts: [{ key: 'contracts', label: $t('A7 Contract register') }] },
    {
        letter: 'A8',
        parts: [
            { key: 'plan_detail', label: $t('A8 Plan detail by plan') },
            { key: 'activities', label: $t('A8 Activity list') },
        ],
    },
    { letter: 'A9', parts: [{ key: 'glossary', label: $t('A9 Glossary and definitions') }] },
    { letter: 'A10', parts: [{ key: 'issues_decisions', label: $t('A10 Issues and decisions') }] },
];

/**
 * The statuses the preview shows as set by hand, across areas, departments
 * and plans.
 */
const manualCount = (statuses) =>
    ['areas', 'departments', 'plans'].reduce((count, group) => count + (statuses?.[group] || []).filter((row) => row.manual).length, 0);

/**
 * The Appendices section's options: one toggle per appendix part. A manual
 * status always includes A0, so its toggle is locked on while any is set and
 * disabled until the first preview has loaded; with the section off, A0 is still included then, and says so.
 */
const AppendicesOptions = ({ options, enabled = true, readOnly, onChange }) => {
    const { preview } = useManagementPackContext();
    const manual = manualCount(preview?.statuses);
    const previewLoaded = preview?.statuses != null;

    if (!enabled) {
        return manual > 0 ? (
            <p className="tw-text-xs tw-text-charcoal-60 tw-mt-2 tw-mb-0">
                {manual === 1
                    ? $t('Appendix A0 is still included because a status is set manually.')
                    : $t('Appendix A0 is still included because {{count}} statuses are set manually.', { count: manual })}
            </p>
        ) : null;
    }

    const toggle = (key) => () => onChange({ ...options, [key]: !options[key] });

    return (
        <div className="tw-flex tw-flex-col tw-mt-3 tw-ps-4 tw-border-s-2 tw-border-charcoal-15">
            {LETTERS().map(({ letter, parts }) => (
                <Group key={letter} title={letter}>
                    {parts.map((part) => {
                        const locked = part.key === 'statuses' && manual > 0;
                        const pending = part.key === 'statuses' && !previewLoaded;

                        return (
                            <Toggle
                                key={part.key}
                                label={part.label}
                                hint={
                                    locked
                                        ? $t('Always included while statuses are set manually ({{count}})', { count: manual })
                                        : pending
                                          ? $t('Available once the preview has loaded')
                                          : null
                                }
                                checked={locked || !!options[part.key]}
                                disabled={readOnly || locked || pending}
                                onChange={toggle(part.key)}
                            />
                        );
                    })}
                </Group>
            ))}
        </div>
    );
};

AppendicesOptions.showWhenDisabled = true;

export default AppendicesOptions;
